import React from 'react';
import { classNames } from 'primereact/utils';

type BadgeProps = {
  label: string;
  variant?: 'primary' | 'secondary';
  state?: 'Brand' | 'Neutral' | 'Warning' | 'Error' | 'Success' | 'Info' | 'Accent';
  size?: 'Large' | 'Small';
  shape?: 'Round' | 'Square';
  statusDot?: boolean;
  iconLeft?: React.ReactNode;
  iconRight?: React.ReactNode;
};

const getBadgeClasses = (props: {
  variant?: BadgeProps['variant'];
  state?: BadgeProps['state'];
  size?: BadgeProps['size'];
  shape?: BadgeProps['shape'];
}) => {
  const { variant = 'primary', state = 'Neutral', size = 'Small', shape = 'Round' } = props;

  const base = 'inline-flex items-center justify-center font-semibold gap-1';

  const sizeMap = {
    Large: 'label-large px-2 py-0.5',
    Small: 'label-small px-2 py-0',
  };

  const shapeMap = {
    Round: 'rounded-round',
    Square: 'rounded-sm',
  };

  const primaryStateMap = {
    Brand: 'bg-surface-brand',
    Neutral: 'bg-surface-inverse-primary',
    Warning: 'bg-surface-warning',
    Error: 'bg-surface-error',
    Success: 'bg-surface-success',
    Info: 'bg-surface-info',
    Accent: 'bg-surface-accent',
  };

  const secondaryStateMap = {
    Brand: 'bg-surface-brand-weak text-text-brand',
    Neutral: 'bg-surface-neutral-weakest text-text-neutral',
    Warning: 'bg-surface-warning-weak text-text-warning',
    Error: 'bg-surface-error-weak text-text-error',
    Success: 'bg-surface-success-weak text-text-success',
    Info: 'bg-surface-info-weakest text-text-info',
    Accent: 'bg-surface-accent-weak text-utility-text-cyan',
  };
  
  if (variant === 'primary') {
      return classNames(base, sizeMap[size], shapeMap[shape], primaryStateMap[state], 'text-text-inverse-primary');
  }

  return classNames(base, sizeMap[size], shapeMap[shape], secondaryStateMap[state]);
};

const secondaryStatusDotColorMap: Record<NonNullable<BadgeProps['state']>, string> = {
    Brand: 'bg-text-brand',
    Neutral: 'bg-text-neutral',
    Warning: 'bg-text-warning',
    Error: 'bg-text-error',
    Success: 'bg-text-success',
    Info: 'bg-text-info',
    Accent: 'bg-utility-text-cyan',
};

export const Badge = ({
  label,
  variant = 'primary',
  state = 'Neutral',
  size,
  shape,
  statusDot,
  iconLeft,
  iconRight,
}: BadgeProps) => {
  const className = getBadgeClasses({ variant, state, size, shape });
  
  const dotColorClass = variant === 'primary' ? 'bg-surface-primary' : secondaryStatusDotColorMap[state];
  const statusDotClass = classNames('h-2 w-2 rounded-full', dotColorClass);

  const iconContainerClass = classNames(
    'flex items-center justify-center w-4 h-4',
    variant === 'primary' && 'text-icon-inverse-primary'
  );

  return (
    <div className={className}>
      {statusDot && <div className={statusDotClass} />}
      {iconLeft && <span className={iconContainerClass}>{iconLeft}</span>}
      <span>{label}</span>
      {iconRight && <span className={iconContainerClass}>{iconRight}</span>}
    </div>
  );
};

export default Badge; 